/**
 * Worker Compatibility Layer
 *
 * Maps the canonical Worker shape (from /api/workers) to the shapes
 * expected by existing page components.
 *
 * Canonical Worker: { id, name, type, trigger, steps, capabilities, tools, status, configuration, metadata, run_count, last_run }
 * Component expects: { id, name, status, steps, outcome_category, automation_type, requires_review, run_count, last_run, tool_preferences, ... }
 */

/**
 * Map a canonical Worker to the shape expected by ServiceAutomations / WorkerCard.
 */
export function toListWorker(w) {
  if (!w) return w;
  return {
    ...w,
    // Fields the card components use directly
    outcome_category: w.configuration?.outcome || _inferOutcomeCategory(w),
    automation_type: w.trigger || w.type || 'event',
    requires_review: w.configuration?.requires_review || false,
    tool_preferences: Array.isArray(w.tools) ? w.tools : Object.keys(w.tools || {}),
    // Keep metadata accessible
    source_collection: w.metadata?.source_collection || 'unknown',
    org_id: w.metadata?.org_id || '',
    // Map behavior for WorkerDetail compatibility
    behavior: w.configuration?.behavior || {},
    agent_id: w.configuration?.agent_id || w.id,
    outcome: w.configuration?.outcome || '',
  };
}

/**
 * Map a canonical Worker to the shape expected by AgentList / AgentDetail.
 */
export function toAgentCompat(w) {
  if (!w) return w;
  return {
    ...w,
    agent_id: w.id,
    description: w.configuration?.description || '',
    short_description: w.configuration?.short_description || '',
    is_active: w.status === 'active',
    agentType: w.type === 'operator' ? 'operator' : w.type === 'voice' ? 'voice' : 'text',
    isOperator: w.type === 'operator',
    accepted_inputs: w.capabilities?.length > 0 ? w.capabilities : ['text'],
    expected_output: 'text',
    // Keep capability_profile as string for AgentAvatar compatibility
    capability_profile: w.configuration?.capability_profile || w.type || '',
    total_runs: w.run_count || 0,
    organization_id: w.metadata?.org_id || '',
  };
}

/**
 * Classify workers into groups for the main list page.
 * Core = event, chat, file, scheduled
 * Specialty = voice, operator
 */
export function groupWorkers(workers) {
  const core = [];
  const specialty = [];
  for (const w of workers) {
    if (w.type === 'voice' || w.type === 'operator') {
      specialty.push(w);
    } else {
      core.push(w);
    }
  }
  return { core, specialty };
}

/**
 * Worker type display config.
 */
export const WORKER_TYPE_LABELS = {
  event: { label: 'Event', color: '#00E5C0', bg: 'rgba(0,229,192,0.08)' },
  chat: { label: 'Chat', color: '#8B8FF5', bg: 'rgba(139,143,245,0.08)' },
  voice: { label: 'Voice', color: '#F59E0B', bg: 'rgba(245,158,11,0.08)' },
  file: { label: 'File', color: '#60A5FA', bg: 'rgba(96,165,250,0.08)' },
  operator: { label: 'Operator', color: '#EC4899', bg: 'rgba(236,72,153,0.08)' },
  scheduled: { label: 'Scheduled', color: '#34D399', bg: 'rgba(52,211,153,0.08)' },
};

function _inferOutcomeCategory(w) {
  const name = (w.name || '').toLowerCase();
  if (name.includes('enquiry') || name.includes('lead')) return 'customer enquiry';
  if (name.includes('file') || name.includes('analysis')) return 'file';
  if (name.includes('follow')) return 'follow-up';
  if (name.includes('review')) return 'review';
  if (name.includes('booking') || name.includes('appointment')) return 'booking';
  if (name.includes('voice')) return 'voice';
  return w.type || 'event';
}
